import { useRef, useMemo, useEffect, Suspense } from 'react';
import { useFrame } from '@react-three/fiber';
import { Group } from 'three';
import * as THREE from 'three/webgpu';
import { uniform } from 'three/tsl';
import { CharacterProps } from './config';
import { useCharacterAssets } from './hooks/useCharacterAssets';
import { useCharacterPhysics } from './hooks/useCharacterPhysics';
import { useGameStore, CameraMode } from '../../core/store/gameStore';
import { CharacterAudio, CharacterAudioHandle } from './CharacterAudio';

export const Character = ({ position = [0, 0, 0], scale = 1, visible = true }: CharacterProps) => {
  const groupRef = useRef<Group>(null);
  const audioRef = useRef<CharacterAudioHandle>(null);

  const hasPrevFrameRef = useRef(false);
  const worldPosRef = useRef(new THREE.Vector3());
  const prevWorldPosRef = useRef(new THREE.Vector3());
  const velocityRef = useRef(new THREE.Vector3());

  const uWorldPos = useMemo(() => uniform(new THREE.Vector3(0, 0, 0)), []);
  const uVelocity = useMemo(() => uniform(new THREE.Vector3(0, 0, 0)), []);

  const setCharacterRef = useGameStore((state) => state.setCharacterRef);
  const { scene, animations, helmets } = useCharacterAssets(uWorldPos);

  // Get camera mode from store
  const cameraMode = useGameStore((state) => state.cameraMode);

  useCharacterPhysics(groupRef, scene, animations, (event) => {
    audioRef.current?.playStep(event.type, event.volume);
  });

  // Publish character ref to global store
  useEffect(() => {
    setCharacterRef(groupRef);
    return () => setCharacterRef(null);
  }, [setCharacterRef]);

  // Spawn at Tunnel View once the Yosemite terrain is loaded, then let the
  // physics hook walk the character on real ground.
  const terrain = useGameStore((state) => state.terrain);
  const spawnedRef = useRef(false);
  useEffect(() => {
    if (!terrain || !groupRef.current || spawnedRef.current) return;
    const spawn = terrain.lonLatToWorld(-119.67738, 37.71562);
    spawn.y = terrain.heightAt(spawn.x, spawn.z) + 1.5;
    groupRef.current.position.copy(spawn);
    spawnedRef.current = true;
  }, [terrain]);

  useEffect(() => {
    if (helmets && helmets.length > 0) {
      const shouldBeVisible = cameraMode !== CameraMode.FPV;
      helmets.forEach((helmet) => {
        helmet.visible = shouldBeVisible;
      });
    }
  }, [cameraMode, helmets]);

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    groupRef.current.updateMatrixWorld(true);
    const worldPos = worldPosRef.current;
    worldPos.setFromMatrixPosition(groupRef.current.matrixWorld);

    uWorldPos.value.set(worldPos.x, worldPos.y, worldPos.z);

    if (hasPrevFrameRef.current) {
      const velocity = velocityRef.current;
      velocity.subVectors(worldPos, prevWorldPosRef.current);
      if (delta > 0) velocity.divideScalar(delta);
      uVelocity.value.set(velocity.x, velocity.y, velocity.z);
    } else {
      uVelocity.value.set(0, 0, 0);
      hasPrevFrameRef.current = true;
    }

    prevWorldPosRef.current.copy(worldPos);
  });

  if (!scene) return null;

  return (
    <group ref={groupRef} position={position} scale={scale} visible={visible} dispose={null}>
      {scene && <primitive object={scene} />}

      <Suspense fallback={null}>
        <CharacterAudio ref={audioRef} />
      </Suspense>
    </group>
  );
};
